import { useCallback, useMemo, useState } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import {
  SystemRoleName,
  SYSTEM_ROLE_NAMES,
} from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { usePermission } from '~/app/auth/hooks/usePermission';
import { RoleService } from '~/app/services/authorization/role.service';
import type { RoleWithPermissionsDto } from '~/app/services/authorization/role.service';
import type { RoleCardRole } from '../types';
import { splitRolesByType, filterRolesVisibleToCaller } from '../utils';
import { resolveAuthorizationErrorMessage } from '../utils/resolve-authorization-error';

function sortSystemRoles<T extends { name: string }>(roles: T[]): T[] {
  const order = new Map<string, number>(
    SYSTEM_ROLE_NAMES.map((name, index) => [name, index]),
  );

  return [...roles].sort(
    (left, right) =>
      (order.get(left.name) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(right.name) ?? Number.MAX_SAFE_INTEGER),
  );
}

type UseRolesPageOptions = {
  openCreateOnMount?: boolean;
};

export function useRolesPage({
  openCreateOnMount = false,
}: UseRolesPageOptions = {}) {
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const { t } = useTranslation('authorization');
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? null;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(openCreateOnMount);
  const [deletingRole, setDeletingRole] = useState<RoleCardRole | null>(null);

  const systemRolesQuery = useQuery({
    queryKey: ['system-roles'],
    queryFn: () => RoleService.listSystemRoles(),
  });

  const tenantRolesQuery = useQuery({
    queryKey: ['roles', organizationId],
    queryFn: () =>
      RoleService.listRoles(organizationId!, { page: 1, limit: 100 }),
    enabled: Boolean(!isSuperAdmin && organizationId),
  });

  const tenantRolesWithPermissionsQuery = useQuery({
    queryKey: ['roles-with-permissions', organizationId],
    queryFn: () => RoleService.listRolesWithPermissions(organizationId!),
    enabled: Boolean(!isSuperAdmin && organizationId),
  });

  const refreshRoles = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['system-roles'] });
    if (!isSuperAdmin && organizationId) {
      void queryClient.invalidateQueries({
        queryKey: ['roles', organizationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['roles-with-permissions', organizationId],
      });
    }
  }, [isSuperAdmin, organizationId, queryClient]);

  const systemRoles = useMemo((): RoleCardRole[] => {
    if (isSuperAdmin) {
      return sortSystemRoles(
        (systemRolesQuery.data ?? []).map((role) => ({
          id: role.id,
          name: role.name,
          isSystemRole: role.isSystemRole,
          organizationId: null,
          usersCount: 0,
          permissionCount: role.permissionKeys.length,
        })),
      );
    }

    const allRoles = tenantRolesQuery.data?.data ?? [];
    const systemPermissionCounts = new Map(
      (systemRolesQuery.data ?? []).map((role) => [
        role.id,
        role.permissionKeys.length,
      ]),
    );

    return sortSystemRoles(
      filterRolesVisibleToCaller(
        splitRolesByType(allRoles).systemRoles.map((role) => ({
          id: role.id,
          name: role.name,
          isSystemRole: role.isSystemRole,
          organizationId: role.organizationId,
          usersCount: role.usersCount,
          permissionCount: systemPermissionCounts.get(role.id),
        })),
        isSuperAdmin,
      ),
    );
  }, [isSuperAdmin, systemRolesQuery.data, tenantRolesQuery.data?.data]);

  const customRoles = useMemo((): RoleCardRole[] => {
    if (isSuperAdmin) return [];

    const allRoles = tenantRolesQuery.data?.data ?? [];
    const customPermissionCounts = new Map(
      (tenantRolesWithPermissionsQuery.data ?? []).map((role) => [
        role.id,
        role.permissionKeys.length,
      ]),
    );

    return splitRolesByType(allRoles).customRoles.map((role) => ({
      id: role.id,
      name: role.name,
      isSystemRole: role.isSystemRole,
      organizationId: role.organizationId,
      usersCount: role.usersCount,
      permissionCount: customPermissionCounts.get(role.id),
    }));
  }, [
    isSuperAdmin,
    tenantRolesQuery.data?.data,
    tenantRolesWithPermissionsQuery.data,
  ]);

  const cloneSourceRoles = useMemo((): RoleWithPermissionsDto[] => {
    if (isSuperAdmin) return [];

    const system = filterRolesVisibleToCaller(
      systemRolesQuery.data ?? [],
      isSuperAdmin,
    );
    const customWithPermissions = (
      tenantRolesWithPermissionsQuery.data ?? []
    ).filter((role) => !role.isSystemRole);

    return [...system, ...customWithPermissions];
  }, [
    isSuperAdmin,
    systemRolesQuery.data,
    tenantRolesWithPermissionsQuery.data,
  ]);

  const fallbackRoles = useMemo((): RoleCardRole[] => {
    if (isSuperAdmin) return [];
    return [...systemRoles, ...customRoles];
  }, [customRoles, isSuperAdmin, systemRoles]);

  const canCreateRole =
    !isSuperAdmin && hasPermission(PermissionKey.ROLES_CREATE);
  const canDeleteRole =
    !isSuperAdmin && hasPermission(PermissionKey.ROLES_DELETE);

  const handleViewRole = useCallback(
    (role: RoleCardRole) => {
      if (role.isSystemRole && isSuperAdmin) {
        navigate(`/admin/roles/${role.id}`);
        return;
      }

      navigate(
        `/admin/roles/${role.id}${organizationId ? `?organizationId=${organizationId}` : ''}`,
      );
    },
    [isSuperAdmin, navigate, organizationId],
  );

  const handleCreateRole = useCallback(
    async (payload: {
      name: string;
      cloneFromRoleId?: string;
      permissionKeys?: PermissionKey[];
    }) => {
      if (!organizationId || isSuperAdmin) return;

      try {
        const created = await RoleService.createRole(organizationId, payload);
        message.success(t('messages.createRoleSuccess'));
        setIsCreateModalOpen(false);
        refreshRoles();
        navigate(`/admin/roles/${created.id}?organizationId=${organizationId}`);
      } catch (error) {
        message.error(
          resolveAuthorizationErrorMessage(
            error,
            t,
            'messages.createRoleError',
          ),
        );
        throw error;
      }
    },
    [isSuperAdmin, message, navigate, organizationId, refreshRoles, t],
  );

  const handleDeleteRole = useCallback(
    async (roleId: string, fallbackRoleId?: string) => {
      if (!organizationId || isSuperAdmin) return;

      try {
        await RoleService.deleteRole(organizationId, roleId, fallbackRoleId);
        message.success(t('messages.deleteRoleSuccess'));
        setDeletingRole(null);
        refreshRoles();
      } catch (error) {
        message.error(
          resolveAuthorizationErrorMessage(
            error,
            t,
            'messages.deleteRoleError',
          ),
        );
        throw error;
      }
    },
    [isSuperAdmin, message, organizationId, refreshRoles, t],
  );

  const isLoading = isSuperAdmin
    ? systemRolesQuery.isLoading
    : tenantRolesQuery.isLoading ||
      tenantRolesWithPermissionsQuery.isLoading ||
      systemRolesQuery.isLoading;

  const queryError = isSuperAdmin
    ? systemRolesQuery.error
    : (tenantRolesQuery.error ??
      tenantRolesWithPermissionsQuery.error ??
      systemRolesQuery.error);

  const error = queryError instanceof Error ? queryError : null;

  return {
    t,
    isSuperAdmin,
    organizationId,
    systemRoles,
    customRoles,
    cloneSourceRoles,
    fallbackRoles,
    isLoading,
    error,
    canCreateRole,
    canDeleteRole,
    userRoleName: user?.roleName ?? '',
    isCreateModalOpen,
    onCreateModalOpenChange: setIsCreateModalOpen,
    deletingRole,
    onDeletingRoleChange: setDeletingRole,
    onViewRole: handleViewRole,
    onCreateRole: handleCreateRole,
    onDeleteRole: handleDeleteRole,
    onRefresh: refreshRoles,
  };
}
